/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback } from 'react';

import { HotelCard } from '../MHotelCard';
import { generateLabel } from './mapHelper';
import { getRateBlock, getCurrentPriceRateBlock } from '../../organisms/OSearchResults/lib/helper';
import {
  STATUS_NOT_AVAILABLE,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
  descriptionToDisplayOnPropertyCard,
  STATUS_AVAILABLE,
} from '../../organisms/OSearchResults/index.constant';
import { addSubDirectoryPrefix } from '../../utils/subDirectoryFunctions';
import { NAVIGATION_URL_CONSTANTS } from '../../organisms/OSearchResults/lib/config';
import { useConditionalPageContextSERP } from '../../hooks';
import { EEO_TYPE } from '../../modules/constants';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { buildChatAvailabilityUrl as buildChatAvailabilityUrlUtil } from '../../modules/utils';

const HotelCardContent = (props: any) => {
  const { NEXT_PUBLIC_AVAILABILITY_SEARCH_URL, pageVal } = useConditionalPageContextSERP();
  const sessionDataValuesOuter = useSearchResultsStore((state: { sessionDataValues: any }) => state.sessionDataValues);
  const chatSearchCriteria =
    sessionDataValuesOuter?.chatSearchCriteria ?? sessionDataValuesOuter?.sessionDataValues?.chatSearchCriteria;
  const { item, index, cardIndex, args } = props;

  // Create a memoized handler to avoid prop mutation during render
  const handleTooltipClick = useCallback(() => {
    args?.setPopupOpenState(true);
    // Note: Removed selectedHotelRef mutation to satisfy React Compiler
    // The parent component should handle this state internally
  }, [args]);

  const ratesData = args?.isFlexibleDateSearch ? null : item?.node?.rates;
  // NLS API (chat context) always returns data at node.property
  // Shop flexible search returns data at node directly
  // Check for node.property first (NLS/chat), then fall back to node (shop flexible)
  const propertyData = item?.node?.property ?? item?.node;

  const unavailableLabel = args?.isSpecialRateSearch
    ? args?.jsonContent.rateUnavailableLabel
    : args?.jsonContent?.soldOutLabel;
  const carouselImages = propertyData?.media;
  const basicInformation = propertyData?.basicInformation;
  const otherPropertyInformation = propertyData?.otherPropertyInformation;
  const brandImageUrl =
    basicInformation?.brand?.photos &&
    basicInformation?.brand?.photos?.length > 0 &&
    basicInformation?.brand?.photos[0]?.content &&
    basicInformation?.brand?.photos[0]?.content?.length > 0 &&
    basicInformation?.brand?.photos[0]?.content[0]?.url;
  let statusCode = STATUS_AVAILABLE;
  const searchState = args?.searchQueryState;
  const pointsRateBlock = getRateBlock(ratesData, 'points');
  const eeoRateBlock = getRateBlock(ratesData, EEO_TYPE);
  const currRateNode = getCurrentPriceRateBlock(searchState, ratesData);
  const showUnavailableMessage = args?.isFlexibleDateSearch
    ? propertyData?.basicInformation?.openingDate
    : !(currRateNode?.rateModes?.lowestAverageRate?.amount || pointsRateBlock?.rateModes?.pointsPerUnit);

  let hotelUnavailable = false;

  if (
    currRateNode?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
    pointsRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
    eeoRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED
  ) {
    statusCode = STATUS_MAX_OCCUPANCY_EXCEEDED;
  } else if (
    currRateNode?.status?.code === STATUS_NOT_AVAILABLE &&
    pointsRateBlock?.status?.code === STATUS_NOT_AVAILABLE
  ) {
    statusCode = STATUS_NOT_AVAILABLE;
  } else if (showUnavailableMessage) {
    hotelUnavailable = true;
  }
  const totalPrice = args?.flexibleOrManyProperty
    ? null
    : args?.isShowFullPrice
    ? currRateNode?.rateModes?.lowestAverageRate?.totalAmount
    : null;
  const buildChatAvailabilityUrl = (propertyCode: string | number, forceRateCalendar = false): string => {
    return buildChatAvailabilityUrlUtil({
      baseUrl: addSubDirectoryPrefix(NEXT_PUBLIC_AVAILABILITY_SEARCH_URL ?? ''),
      propertyCode,
      chatSearchCriteria,
      fallbackSearchOptions: args?.searchQueryState?.search?.options,
      fallbackUseRewardsPoints: args?.searchQueryState?.points ?? false,
      fallbackFlexibleDate: args?.isFlexibleDateSearch ?? false,
      forceRateCalendar,
    });
  };

  return (
    <HotelCard
      id={propertyData?.id}
      cardIndex={cardIndex}
      imageUrl={args?.imageURL !== '' ? args?.imageURL : args?.fallbackImages.Square}
      name={propertyData?.basicInformation?.name}
      nameInDefaultLanguage={propertyData?.basicInformation?.nameInDefaultLanguage}
      stars={propertyData?.reviews?.stars.count}
      totalReviews={propertyData?.reviews?.numberOfReviews?.count}
      distance={args?.isFlexibleDateSearch ? item?.distance : item?.node?.distance}
      description={
        propertyData?.basicInformation?.descriptions?.find(
          (description: { type: { enumCode: string } }) =>
            description?.type?.enumCode === descriptionToDisplayOnPropertyCard
        )?.text
      }
      price={{ ...item }}
      currencyCode={args?.searchQueryState?.currentCurrency}
      searchQueryContextValue={args?.searchQueryState}
      staticContent={args?.jsonContent}
      currentLocaleLang={args?.currentLocale}
      duration={args?.isFlexibleDateSearch ? null : item?.node.rates?.[0]?.rateAmounts?.[0]?.rateUnit?.code}
      hotelUnavailable={hotelUnavailable}
      hotelUnavailableLabel={args?.flexibleOrManyProperty ? '' : unavailableLabel}
      openingDate={propertyData?.basicInformation?.openingDate}
      points={args?.flexibleOrManyProperty ? null : pointsRateBlock}
      pointSavers={args?.flexibleOrManyProperty ? null : item?.node.pointSavers}
      adultsOnly={propertyData?.basicInformation?.isAdultsOnly}
      selected={() => {
        args?.setSelectedHotel([]);
      }}
      nameUrl={`${addSubDirectoryPrefix(NAVIGATION_URL_CONSTANTS.SM_HOTEL_QUICK_VIEW_URL)}?propertyId=${
        propertyData?.id
      }&brandCode=${propertyData?.basicInformation?.brand?.id}&marshaCode=${propertyData?.id}`}
      reviewUrl={`${addSubDirectoryPrefix(NAVIGATION_URL_CONSTANTS.SM_HOTEL_QUICK_VIEW_URL)}?propertyId=${
        propertyData?.id
      }&brandCode=${propertyData?.basicInformation?.brand?.id}&marshaCode=${propertyData?.id}`}
      ratesUrl={
        pageVal === 'chat'
          ? buildChatAvailabilityUrl(propertyData?.id, args?.isFlexibleDateSearch)
          : args?.isFlexibleDateSearch
          ? `${addSubDirectoryPrefix(
              `${NEXT_PUBLIC_AVAILABILITY_SEARCH_URL}${NAVIGATION_URL_CONSTANTS.SM_VIEW_RATES_ADF_URL}`
            )}${propertyData?.id}&isSearch=true&currency=`
          : `${addSubDirectoryPrefix(
              `${NEXT_PUBLIC_AVAILABILITY_SEARCH_URL}${NAVIGATION_URL_CONSTANTS.SM_VIEW_RATES_URL}`
            )}${propertyData?.id}&isSearch=true&showFullPrice=${args?.isShowFullPrice}${
              args?.searchQueryState?.currentCurrency !== 'default' ? '&redirectedFromSERP=true' : ''
            }`
      }
      availabilityUrl={`${addSubDirectoryPrefix(
        `${NEXT_PUBLIC_AVAILABILITY_SEARCH_URL}${NAVIGATION_URL_CONSTANTS.SM_AVAILABILITY_URL}`
      )}${propertyData?.id}`}
      index={index}
      selectedHotelData={args?.selectedHotel}
      hotelPromoLabel={
        generateLabel(
          args?.isFlexibleDateSearch ? item?.node : item?.node?.property,
          true,
          args?.selectedHotel,
          args?.isFlexibleDateSearch,
          args?.jsonContent
        ).label
      }
      lengthOfStay={args?.flexibleOrManyProperty ? null : item?.node?.rates[0]?.lengthOfStay}
      totalPrice={totalPrice}
      calltoBookLabel={args?.jsonContent.callToBookLabel}
      openingSoonLabel={args?.jsonContent.openingSoonLabel}
      monthsArray={args?.jsonContent?.monthsShort}
      avgPerNightLabel={args?.jsonContent.avgPerNightLabel}
      perNightLabel={args?.jsonContent.perNightLabel}
      adultsOnlyMessageLabel={args?.jsonContent.adultsOnlyMessageLabel}
      removeChildrenMessage={args?.jsonContent.removeChildrenMessage}
      pointsSaverLabel={args?.jsonContent.pointsSaverLabel}
      pointsPerStayLabel={args?.jsonContent.pointsPerStayLabel}
      handleTooltipClickOnCard={handleTooltipClick}
      childCount={args?.childCount}
      flexibleSearch={args?.isFlexibleDateSearch}
      isHotelBookable={propertyData?.basicInformation?.bookable}
      brandImageUrl={brandImageUrl}
      carouselImages={carouselImages}
      idx={index}
      basicInformation={basicInformation}
      otherPropertyInformation={otherPropertyInformation}
      statusCode={statusCode}
      editToSeeRatesLabel={args?.jsonContent?.editToSeeRatesLabel}
      editGuestsOrRoomsLabel={args?.jsonContent?.editGuestsOrRoomsLabel}
      editGuestsOrRoomsLabelRates={args?.jsonContent?.editGuestsOrRoomsLabelRates}
      maximumGuestsExceededLabel={args?.jsonContent?.maximumGuestsExceededLabel}
      isSpecialRateSearch={args?.isSpecialRateSearch}
      flexibleOrManyProperty={args?.flexibleOrManyProperty}
      isErrorState={args?.isErrorState}
      ratesData={args?.isFlexibleDateSearch ? null : item?.node?.rates}
      options={args?.options}
      isFlexibleDateSearch={args?.isFlexibleDateSearch}
      isMax={propertyData?.basicInformation?.isMax}
      noRatesAvailableForAnyProperty={args?.noRatesAreAvailable?.noRatesAvailableForAnyProperty}
      onOpenHotelPanel={props?.onOpenHotelPanel}
      isSinglePropertyResponse={args?.isSinglePropertyResponse}
      contactInformation={propertyData?.contactInformation}
    />
  );
};

export default HotelCardContent;
